import { useEffect, useMemo, useRef, useState } from "react";
import { Award, Printer, RotateCcw, Save, CheckCircle2, LogIn } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  {
    id: "destination",
    name: "Destination",
    weight: 15,
    intro: "Access, visibility and destination connection.",
    groups: [
      {
        title: "Location & Accessibility",
        questions: [
          "Is the property easily accessible from airports, ports, train stations, or major transportation routes?",
          "Is adequate parking available for guests?",
          "Is the property accessible for guests with disabilities?",
          "Is public transportation conveniently available nearby?",
          "Are directional signs and wayfinding clear and effective?",
        ],
      },
      {
        title: "Website & Digital Presence",
        questions: [
          "Does the property have a professional and up-to-date website?",
          "Is the website mobile-friendly and easy to navigate?",
          "Is direct online booking available?",
          "Is property information accurate, complete, and regularly updated?",
          "Is the website available in multiple languages?",
        ],
      },
      {
        title: "Social Media & Online Reputation",
        questions: [
          "Is the property active on relevant social media platforms?",
          "Are guest reviews monitored and responded to professionally?",
          "Is branding consistent across all digital channels?",
          "Does the property maintain strong online review ratings?",
          "Does the property actively engage with its online audience?",
        ],
      },
      {
        title: "Destination Experience",
        questions: [
          "Does the property provide destination information before arrival?",
          "Are local attractions, events, and experiences actively promoted?",
          "Does the property assist guests with planning activities and excursions?",
          "Are local transportation options clearly communicated?",
          "Does the property act as a strong ambassador for the destination?",
        ],
      },
    ],
  },
  {
    id: "hospitality",
    name: "Hospitality",
    weight: 30,
    intro: "Service culture, guest delight and memorable hospitality.",
    groups: [
      {
        title: "Guest Experience",
        questions: [
          "Are guests welcomed professionally upon arrival?",
          "Are check-in and check-out processes efficient, professional, and guest-friendly?",
          "Are guest preferences recorded, recognized, and used to personalize the experience?",
          "Is guest satisfaction consistently measured?",
          "Are guest complaints resolved effectively?",
          "Is service delivered consistently throughout the stay?",
          "Are guest expectations regularly exceeded?",
          "Is personalized service evident throughout the guest journey?",
          "Are returning guests recognized appropriately?",
          "Is guest privacy respected and protected?",
        ],
      },
      {
        title: "Customer Service",
        questions: [
          "Are guest inquiries handled professionally and within established response times?",
          "Is there a documented complaint-management process?",
          "Are follow-up communications sent after departure?",
          "Is service recovery practiced when issues arise?",
          "Is staff empowered to resolve guest concerns?",
          "Is guest feedback reviewed and used to drive continuous improvement?",
          "Are guest requests fulfilled promptly?",
          "Does management demonstrate a strong customer-service culture?",
        ],
      },
      {
        title: "Food, Beverage & Gastronomy",
        questions: [
          "Is food quality consistently high?",
          "Is beverage quality consistently high?",
          "Are local ingredients and products used whenever possible?",
          "Is menu diversity appropriate for the target market?",
          "Are food presentation standards maintained?",
          "Are food safety procedures documented and followed?",
          "Does the property showcase local gastronomy and culinary traditions?",
          "Does the property offer signature dishes, tastings, or culinary experiences?",
          "Does the dining experience contribute significantly to the destination experience?",
          "Does the property demonstrate strong culinary leadership through a recognized chef or executive culinary team?",
          "Has the property received Michelin recognition or other respected culinary awards?",
        ],
      },
      {
        title: "Staff Excellence",
        questions: [
          "Are staff professionally presented?",
          "Do staff demonstrate strong hospitality skills, product knowledge, and destination expertise?",
          "Are employees trained regularly?",
          "Are multiple languages spoken by staff?",
          "Does the property actively measure employee engagement and maintain strong staff retention?",
        ],
      },
      {
        title: "Hospitality Excellence & Guest Delight",
        questions: [
          "Does the property demonstrate exceptional attention to detail through personalized guest touches?",
          "Does the property offer unique features or experiences that distinguish it from competitors?",
          "Does the property consistently go the extra mile to exceed guest expectations?",
          "Does the property create memorable experiences and emotional connections with guests?",
          "Does the property provide exceptional comfort, convenience, and guest wellbeing?",
          "Does the property consistently create a guest experience that inspires recommendations and a strong wow factor?",
        ],
      },
    ],
  },
  {
    id: "property",
    name: "Property Quality",
    weight: 20,
    intro: "Condition, comfort, design and physical excellence.",
    groups: [
      {
        title: "Cleanliness",
        questions: [
          "Are guest rooms consistently clean and well-presented?",
          "Are public areas consistently clean?",
          "Are restrooms maintained to high standards?",
          "Are housekeeping procedures documented and followed?",
          "Are cleanliness inspections conducted regularly?",
        ],
      },
      {
        title: "Maintenance",
        questions: [
          "Is the property free from visible defects and deterioration?",
          "Is preventive maintenance performed regularly?",
          "Are maintenance issues resolved promptly?",
          "Are guest facilities fully functional?",
          "Are landscaping and exterior areas well maintained?",
        ],
      },
      {
        title: "Guest Rooms",
        questions: [
          "Are beds and sleep facilities comfortable and of high quality?",
          "Are furniture, fixtures, and equipment maintained to a high standard?",
          "Is room technology functional and up to date?",
          "Is soundproofing adequate for guest comfort?",
          "Are climate-control systems effective and reliable?",
        ],
      },
      {
        title: "Design & Presentation",
        questions: [
          "Does the property demonstrate attractive design and a distinctive visual identity?",
          "Does the architecture complement the destination?",
          "Do public spaces create a positive and welcoming first impression?",
          "Is lighting appropriate and well designed?",
          "Are premium amenities and high-quality materials consistently provided throughout the property?",
          "Does the property demonstrate quality craftsmanship?",
          "Is the overall physical presentation consistent and professional?",
          "Are public spaces inviting and comfortable?",
          "Does the property reflect its intended market positioning?",
          "Is the overall physical environment exceptional?",
        ],
      },
    ],
  },
  {
    id: "heritage-community",
    name: "Heritage & Local Community",
    weight: 15,
    intro: "Authenticity, culture and positive local contribution.",
    groups: [
      {
        title: "Culture & Heritage",
        questions: [
          "Does the property actively promote local culture through authentic experiences and storytelling?",
          "Does the property preserve and effectively communicate local history and heritage?",
          "Are cultural traditions incorporated into the guest experience?",
          "Does the property support local artists, performers, or cultural organizations?",
          "Are heritage assets protected and maintained?",
        ],
      },
      {
        title: "Architecture & Identity",
        questions: [
          "Does the property reflect local architectural character?",
          "Does the property contribute positively to destination identity?",
          "Does the property enhance the attractiveness and prestige of the destination?",
          "Does the property create a strong sense of place?",
        ],
      },
      {
        title: "Local Community",
        questions: [
          "Does the property prioritize local hiring?",
          "Are local suppliers utilized whenever possible?",
          "Are local products promoted and sold to guests?",
          "Does the property support community initiatives?",
          "Does management engage with local tourism stakeholders?",
          "Does the property contribute positively to the local economy and community?",
          "Is the property recognized as a positive community partner?",
          "Does the property support local events and festivals?",
          "Does the property encourage guests to explore local businesses?",
          "Does the property contribute to preserving local identity and traditions?",
          "Does tourism generated by the property provide measurable benefits to the local community?",
        ],
      },
    ],
  },
  {
    id: "safety",
    name: "Safety",
    weight: 10,
    intro: "Preparedness, protection and trusted operations.",
    groups: [
      {
        title: "Health & Emergency Preparedness",
        questions: [
          "Are hygiene and food safety standards documented, monitored, and verified?",
          "Is there accessible hospital or emergency medical care nearby?",
          "Are guests informed about emergency medical services and contacts?",
          "Are first-aid resources available and maintained?",
          "Are staff trained in emergency response procedures?",
        ],
      },
      {
        title: "Security & Risk Management",
        questions: [
          "Has the property implemented security risk assessments and measures to prevent theft, robbery, violence, kidnapping, terrorism, and other threats?",
          "Are appropriate security systems in place?",
          "Are guest rooms and public areas secure?",
          "Are emergency procedures documented and communicated?",
          "Are crisis-management and business-continuity plans maintained?",
        ],
      },
      {
        title: "Compliance & Safety Culture",
        questions: [
          "Does the property comply with all applicable legal and licensing requirements?",
          "Are formal risk assessments conducted regularly?",
          "Are safety incidents recorded and reviewed?",
          "Does management actively promote a culture of safety?",
          "Does the property provide a safe, secure, and trustworthy environment for guests and staff?",
        ],
      },
    ],
  },
  {
    id: "sustainability",
    name: "Sustainability",
    weight: 10,
    intro: "Responsible tourism, conservation and measurable stewardship.",
    groups: [
      {
        title: "Sustainability Performance",
        questions: [
          "Is energy consumption monitored and actively reduced through efficiency initiatives?",
          "Is renewable energy used where practical?",
          "Is water consumption monitored and reduced through conservation measures?",
          "Are wastewater-management practices implemented?",
          "Is waste reduction actively practiced?",
          "Is recycling implemented effectively?",
          "Is sustainable purchasing practiced?",
          "Are local and environmentally responsible suppliers preferred?",
          "Are single-use plastics minimized?",
          "Are carbon-reduction initiatives implemented?",
          "Are biodiversity and natural resources protected?",
          "Are guests informed about sustainability initiatives?",
          "Are staff trained in sustainability practices?",
          "Are sustainability goals measured, reviewed, and communicated?",
          "Does the property demonstrate leadership in responsible tourism?",
        ],
      },
    ],
    auditorReview: [
      "Would you confidently recommend this property to international travelers seeking an exceptional experience?",
      "Does the property positively represent its destination, region, or country?",
      "Does the property demonstrate world-class hospitality standards?",
      "Does the property create memorable experiences that distinguish it from competitors?",
      "Is the property a benchmark of excellence within its market segment?",
    ],
  },
];

// Flatten question metadata once
const QUESTION_META = [];
CATEGORIES.forEach((cat) => {
  cat.groups.forEach((g) => {
    g.questions.forEach(() => QUESTION_META.push({ category: cat.id }));
  });
});

const initialScores = () => {
  const obj = {};
  QUESTION_META.forEach((_, i) => {
    obj[`q${i + 1}`] = "";
  });
  return obj;
};

const initialAuditor = () => {
  const obj = {};
  CATEGORIES.forEach((cat) => {
    if (cat.auditorReview) {
      cat.auditorReview.forEach((_, idx) => {
        obj[`${cat.id}_aud${idx + 1}`] = false;
      });
    }
  });
  return obj;
};

const QuestionnairePage = () => {
  const { user, token } = useAuth();
  const [currentCategoryIdx, setCurrentCategoryIdx] = useState(0);
  const [scores, setScores] = useState(initialScores);
  const [auditor, setAuditor] = useState(initialAuditor);
  const [loading, setLoading] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const hydratedRef = useRef(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load existing draft on mount (only if signed in)
  useEffect(() => {
    if (!token) {
      hydratedRef.current = true;
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/assessment/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled || !res.data) return;
        if (res.data.scores) setScores({ ...initialScores(), ...res.data.scores });
        if (res.data.auditor) setAuditor({ ...initialAuditor(), ...res.data.auditor });
        if (res.data.updated_at) setSavedAt(res.data.updated_at);
        if (res.data.submitted) {
          setSubmitted(true);
          setSubmittedAt(res.data.submitted_at);
        }
      } catch (_e) {
        // ignore
      } finally {
        if (!cancelled) {
          setLoading(false);
          hydratedRef.current = true;
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Map of question numbers (global) per category for rendering
  const questionStartIdx = useMemo(() => {
    const map = {};
    let i = 1;
    CATEGORIES.forEach((cat) => {
      map[cat.id] = i;
      cat.groups.forEach((g) => {
        i += g.questions.length;
      });
    });
    return map;
  }, []);

  const categoryPercents = useMemo(() => {
    const out = {};
    CATEGORIES.forEach((cat) => {
      const ids = QUESTION_META.map((m, i) => (m.category === cat.id ? `q${i + 1}` : null)).filter(Boolean);
      const total = ids.reduce((s, id) => s + (scores[id] === "" ? 0 : Number(scores[id])), 0);
      out[cat.id] = ids.length ? (total / (ids.length * 5)) * 100 : 0;
    });
    return out;
  }, [scores]);

  const overall = useMemo(() => {
    let weighted = 0;
    CATEGORIES.forEach((cat) => {
      weighted += categoryPercents[cat.id] * (cat.weight / 100);
    });
    return Math.round(weighted);
  }, [categoryPercents]);

  const completion = useMemo(() => {
    const answered = Object.values(scores).filter((v) => v !== "").length;
    return Math.round((answered / QUESTION_META.length) * 100);
  }, [scores]);

  const certification = useMemo(() => {
    const catScores = Object.values(categoryPercents);
    const minCat = catScores.length ? Math.min(...catScores) : 0;
    const allAuditorAnswered = Object.values(auditor).every((v) => v === true);
    if (completion < 100) {
      return { label: "In Progress", sub: "Complete the assessment to qualify", color: "#8a8f93" };
    }
    if (overall >= 90 && minCat >= 85 && allAuditorAnswered) {
      return { label: "Gold Certified", sub: "World-Class Excellence", color: "#b88a35" };
    }
    if (overall >= 80 && minCat >= 70) {
      return { label: "Silver Certified", sub: "Certified Excellence", color: "#7b8790" };
    }
    if (overall >= 70 && minCat >= 60) {
      return { label: "Bronze Certified", sub: "Certified Quality", color: "#9b6748" };
    }
    return { label: "Not Yet Certified", sub: "Thresholds not currently met", color: "#8b5a55" };
  }, [overall, completion, categoryPercents, auditor]);

  const handleScoreChange = (qid, value) => {
    setScores((prev) => ({ ...prev, [qid]: value }));
  };

  const handleAuditorToggle = (key, checked) => {
    setAuditor((prev) => ({ ...prev, [key]: checked }));
  };

  // Debounced autosave (only when signed in & after initial hydration)
  useEffect(() => {
    if (!token || !hydratedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.put(
          `${API}/assessment/me`,
          {
            scores,
            auditor,
            category_percents: categoryPercents,
            overall,
            completion,
            certification_label: certification.label,
            certification_sub: certification.sub,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.updated_at) setSavedAt(res.data.updated_at);
      } catch (_e) {
        // silent fail; UI keeps state
      }
    }, 900);
    return () => clearTimeout(saveTimerRef.current);
  }, [scores, auditor, token, categoryPercents, overall, completion, certification.label, certification.sub]);

  const handleSubmit = async () => {
    if (!token) {
      toast.error("Please sign in to submit your assessment");
      return;
    }
    if (completion < 100) {
      toast.error("Complete all 135 questions before submitting");
      return;
    }
    try {
      await axios.post(`${API}/assessment/me/submit`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSubmitted(true);
      setSubmittedAt(new Date().toISOString());
      toast.success("Assessment submitted to WTTQ.Org for review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    }
  };

  const handleReset = async () => {
    setScores(initialScores());
    setAuditor(initialAuditor());
    setSubmitted(false);
    setSubmittedAt(null);
  };

  const handlePrint = () => window.print();

  const currentCategory = CATEGORIES[currentCategoryIdx];

  return (
    <div className="min-h-screen" data-testid="questionnaire-page">
      <style>{`
        .q-page { font-family: 'DM Sans', system-ui, sans-serif; color:#102331; background:#f4f0e8; }
        .q-serif { font-family: 'Playfair Display', serif; }
        .q-page-shell {
          background:
            radial-gradient(circle at top right, rgba(199,155,91,.08), transparent 28%),
            linear-gradient(180deg,#f8f5ef 0%,#f3eee5 100%);
        }
        .q-hero {
          position:relative; overflow:hidden;
          background:linear-gradient(90deg,rgba(9,25,35,.96) 0%,rgba(9,25,35,.9) 45%,rgba(9,25,35,.55) 100%);
          min-height:260px;
        }
        .q-hero-grid {
          position:absolute; inset:0;
          background-image:
            linear-gradient(rgba(255,255,255,.05) 1px,transparent 1px),
            linear-gradient(90deg,rgba(255,255,255,.05) 1px,transparent 1px);
          background-size:44px 44px; opacity:.18;
        }
        .q-crest {
          width:58px; height:58px; border:1px solid rgba(255,255,255,.45);
          border-radius:50%; display:flex; align-items:center; justify-content:center;
          color:#e0bf84; font-family:'Playfair Display',serif; font-size:18px; font-weight:700;
          letter-spacing:.08em; flex-shrink:0;
        }
        .q-summary-card { background:rgba(255,255,255,.95); border:1px solid rgba(232,222,206,.95);
          box-shadow:0 14px 32px rgba(31,48,58,.08); }
        .q-summary-grid { transform:translateY(-34px); }
        .q-main-card { background:rgba(255,255,255,.95); border:1px solid #e8dece;
          box-shadow:0 16px 40px rgba(31,48,58,.06); }
        .q-tab { border:1px solid #e8dece; color:#56636b; background:#fffefa; transition:.2s ease; }
        .q-tab:hover { border-color:#d5c2a5; color:#102331; }
        .q-tab-active { background:#102331; color:white; border-color:#102331; }
        .q-row { border-bottom:1px solid #efe7dc; transition:.2s ease; }
        .q-row:last-child { border-bottom:none; }
        .q-row:hover { background:#fbf8f3; }
        .q-num {
          width:34px; height:34px; border-radius:50%;
          background:#f3eadf; color:#8a6b43;
          display:flex; align-items:center; justify-content:center;
          font-size:12px; font-weight:700; flex-shrink:0;
        }
        .q-select {
          appearance:none;
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' fill='none' viewBox='0 0 24 24' stroke='%23646b70' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
          background-repeat:no-repeat; background-position:right .7rem center; background-size:15px;
        }
        .q-ring {
          width:154px; height:154px; border-radius:50%;
          display:flex; align-items:center; justify-content:center;
          transition: background .4s ease;
        }
        .q-ring-inner {
          width:118px; height:118px; border-radius:50%; background:#fffdf8;
          display:flex; align-items:center; justify-content:center; flex-direction:column;
        }
        .q-bar-track { height:8px; background:#efe8de; border-radius:999px; overflow:hidden; }
        .q-bar-fill { height:100%; background:linear-gradient(90deg,#b89158,#d6b679); border-radius:999px; transition:width .35s ease; }
        .q-auditor-section { background:linear-gradient(135deg,#fdf5ea,#fffefb);
          border:1px solid #e7d7bc; border-radius:1.5rem; margin-top:2rem; }
        .q-auditor-row { border-bottom:1px solid #ebe2d6; padding:1rem 1.5rem;
          display:flex; align-items:center; gap:1rem; }
        .q-auditor-row:last-child { border-bottom:none; }
        .q-auditor-row label { flex:1; cursor:pointer; font-size:.9375rem; color:#30414b; }
        .q-auditor-checkbox { width:20px; height:20px; flex-shrink:0; cursor:pointer; accent-color:#c79b5b; }
        @media print {
          .q-no-print { display:none !important; }
          .q-page, .q-page-shell { background:white; }
          .q-summary-grid { transform:none; margin-top:20px; }
        }
      `}</style>

      <Header />

      <div className="q-page">
        <div className="q-page-shell w-full pt-20">
          {/* Hero */}
          <header className="q-hero w-full" data-testid="questionnaire-hero">
            <div className="q-hero-grid" />
            <div className="relative z-[2] max-w-7xl mx-auto px-6 lg:px-8 py-10">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-center gap-5">
                  <div className="q-crest">WQ</div>
                  <div>
                    <p className="text-xs tracking-[0.22em] uppercase text-[#d9bf8f] font-semibold mb-2">
                      WTTQ.Org · Global Tourism & Hospitality Excellence
                    </p>
                    <h1 className="q-serif text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
                      Certification Questionnaire
                    </h1>
                    <p className="text-[#d7dee1] mt-3 max-w-2xl text-sm sm:text-base">
                      Assess your property against the WTTQ.Org framework — six
                      pillars, 135 evaluation criteria, scored 0–5. Results
                      update live and can be exported to PDF.
                    </p>
                  </div>
                </div>
                <div className="self-start md:self-center">
                  <div className="px-4 py-2 rounded-full border border-[#d9bf8f]/50 text-[#f0d8ab] text-xs uppercase tracking-[0.16em]">
                    Self-Assessment Tool
                  </div>
                </div>
              </div>
            </div>
          </header>

          <main className="max-w-7xl mx-auto px-6 lg:px-8 pb-16">
            {/* Summary cards */}
            <section className="q-summary-grid grid md:grid-cols-4 gap-4 relative z-10" data-testid="summary-grid">
              <article className="q-summary-card rounded-2xl p-5">
                <p className="text-xs uppercase tracking-[0.16em] text-[#8a7356] font-semibold">
                  Overall Score
                </p>
                <div className="flex items-end gap-2 mt-3">
                  <span className="text-4xl font-semibold" data-testid="overall-score-top">
                    {overall}
                  </span>
                  <span className="text-sm text-[#7b858a] mb-1">/100</span>
                </div>
              </article>
              <article className="q-summary-card rounded-2xl p-5">
                <p className="text-xs uppercase tracking-[0.16em] text-[#8a7356] font-semibold">
                  Certification
                </p>
                <div
                  className="mt-3 text-xl font-semibold"
                  style={{ color: certification.color }}
                  data-testid="certification-top"
                >
                  {certification.label}
                </div>
              </article>
              <article className="q-summary-card rounded-2xl p-5">
                <p className="text-xs uppercase tracking-[0.16em] text-[#8a7356] font-semibold">
                  Completion
                </p>
                <div className="flex items-end gap-2 mt-3">
                  <span className="text-4xl font-semibold" data-testid="completion-top">
                    {completion}
                  </span>
                  <span className="text-sm text-[#7b858a] mb-1">%</span>
                </div>
              </article>
              <article className="q-summary-card rounded-2xl p-5">
                <p className="text-xs uppercase tracking-[0.16em] text-[#8a7356] font-semibold">
                  Note
                </p>
                <p className="text-sm text-[#5f6a70] mt-3 leading-5">
                  This is an indicative self-assessment. Final certification
                  requires WTTQ.Org review and audit.
                </p>
              </article>
            </section>

            {/* Main layout */}
            <div className="grid lg:grid-cols-[1fr_340px] gap-6">
              {/* Main category panel */}
              <section className="q-main-card rounded-3xl overflow-hidden">
                <div className="p-6 border-b border-[#ebe2d6]">
                  <p className="tracking-[0.12em] text-xs uppercase text-[#a17d4f] font-semibold mb-2">
                    Excellence Framework · Six Pillars
                  </p>
                  <h2 className="q-serif text-2xl sm:text-3xl text-[#102331]">
                    {currentCategory.name}
                  </h2>
                  <div className="flex flex-wrap gap-2 mt-5 q-no-print" data-testid="category-tabs">
                    {CATEGORIES.map((cat, idx) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setCurrentCategoryIdx(idx)}
                        className={`q-tab rounded-full px-4 py-2 text-sm font-medium ${
                          idx === currentCategoryIdx ? "q-tab-active" : ""
                        }`}
                        data-testid={`category-tab-${cat.id}`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-6" data-testid="category-content">
                  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-[#9f7a4e] font-semibold">
                        Category {currentCategoryIdx + 1} · {currentCategory.weight}% Weight
                      </p>
                      <h3 className="q-serif text-2xl mt-2">{currentCategory.name}</h3>
                      <p className="text-sm text-[#6f7b81] mt-2">{currentCategory.intro}</p>
                    </div>
                    <div className="text-sm text-[#6b7479]">
                      Scoring scale: <span className="font-semibold text-[#102331]">0–5</span>
                    </div>
                  </div>

                  {(() => {
                    let qNum = questionStartIdx[currentCategory.id];
                    return currentCategory.groups.map((group) => (
                      <div key={group.title} className="mb-8">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-px bg-[#cba76d]" />
                          <h4 className="text-xs uppercase tracking-[0.18em] font-semibold text-[#8c6b43]">
                            {group.title}
                          </h4>
                        </div>
                        <div className="border border-[#eee5da] rounded-2xl overflow-hidden">
                          {group.questions.map((q) => {
                            const num = qNum++;
                            const qid = `q${num}`;
                            return (
                              <div
                                key={qid}
                                className="q-row p-4 flex flex-col md:flex-row md:items-center gap-4"
                              >
                                <div className="flex gap-3 flex-1">
                                  <div className="q-num">{num}</div>
                                  <p className="text-sm leading-5 text-[#30414b] pt-1">{q}</p>
                                </div>
                                <div className="w-full md:w-36">
                                  <label className="sr-only" htmlFor={qid}>
                                    Score for question {num}
                                  </label>
                                  <select
                                    id={qid}
                                    className="q-select w-full border border-[#ddd1c2] rounded-xl px-3 py-2.5 text-sm bg-white text-[#263845]"
                                    value={scores[qid]}
                                    onChange={(e) => handleScoreChange(qid, e.target.value)}
                                    data-testid={`score-${qid}`}
                                  >
                                    <option value="">Score</option>
                                    <option value="0">0 · N/A</option>
                                    <option value="1">1 · Poor</option>
                                    <option value="2">2 · Basic</option>
                                    <option value="3">3 · Good</option>
                                    <option value="4">4 · Very Good</option>
                                    <option value="5">5 · Excellent</option>
                                  </select>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}

                  {currentCategory.auditorReview && (
                    <div className="q-auditor-section" data-testid="auditor-section">
                      <div className="p-5 border-b border-[#e7d7bc]">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-9 h-9 rounded-full bg-[#d8b274] text-white flex items-center justify-center">
                            <Award className="w-[18px] h-[18px]" />
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-[0.18em] text-[#9b7545] font-semibold">
                              Gold Excellence Review
                            </p>
                            <h4 className="q-serif text-lg text-[#15303f]">
                              Required for Gold certification
                            </h4>
                          </div>
                        </div>
                        <p className="text-sm text-[#6f7b81] mt-2">
                          Auditor evaluation · non-scored
                        </p>
                      </div>
                      <div className="border border-[#eadcc9] rounded-xl overflow-hidden bg-white">
                        {currentCategory.auditorReview.map((q, idx) => {
                          const key = `${currentCategory.id}_aud${idx + 1}`;
                          return (
                            <div key={key} className="q-auditor-row">
                              <input
                                type="checkbox"
                                id={key}
                                className="q-auditor-checkbox"
                                checked={auditor[key] || false}
                                onChange={(e) => handleAuditorToggle(key, e.target.checked)}
                                data-testid={`auditor-${key}`}
                              />
                              <label htmlFor={key}>{q}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-6 pt-5 q-no-print">
                  {!token ? (
                    <div
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-[#fbf7f0] border border-[#eadfce] rounded-2xl px-4 py-3"
                      data-testid="signin-banner"
                    >
                      <p className="text-sm text-[#6f5635]">
                        <strong>Sign in to save your progress</strong> — your answers will sync to your account and you can submit when complete.
                      </p>
                      <Link
                        to="/signin"
                        className="inline-flex items-center gap-2 bg-[#102331] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#173747] transition"
                        data-testid="banner-signin-link"
                      >
                        <LogIn className="w-4 h-4" />
                        Sign in
                      </Link>
                    </div>
                  ) : submitted ? (
                    <div
                      className="flex items-center gap-3 bg-[#e9f3ee] border border-[#bcdcc7] rounded-2xl px-4 py-3 text-sm text-[#2e5b3f]"
                      data-testid="submitted-banner"
                    >
                      <CheckCircle2 className="w-5 h-5 text-[#4a7b68]" />
                      <span>
                        Submitted to WTTQ.Org for review
                        {submittedAt
                          ? ` · ${new Date(submittedAt).toLocaleDateString(undefined, { dateStyle: "medium" })}`
                          : ""}
                        . You can still edit; changes will resave automatically.
                      </span>
                    </div>
                  ) : (
                    <div
                      className="flex items-center gap-2 text-xs text-[#7a858a]"
                      data-testid="save-status"
                    >
                      <Save className="w-3.5 h-3.5 text-[#c79b5b]" />
                      {loading
                        ? "Loading your assessment..."
                        : savedAt
                        ? `Saved · ${new Date(savedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`
                        : "Changes will auto-save to your account"}
                    </div>
                  )}
                </div>

                <div className="p-6 border-t border-[#ebe2d6] mt-3 flex flex-col sm:flex-row gap-3 justify-between q-no-print">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="px-5 py-3 rounded-xl border border-[#d9c8b1] text-[#6b5843] hover:bg-[#f8f2ea] transition flex items-center justify-center gap-2"
                    data-testid="reset-btn"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset Assessment
                  </button>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={handlePrint}
                      className="px-5 py-3 rounded-xl border border-[#102331] text-[#102331] hover:bg-[#102331] hover:text-white transition flex items-center justify-center gap-2"
                      data-testid="print-btn"
                    >
                      <Printer className="w-4 h-4" />
                      Print / Save as PDF
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={!token || completion < 100 || submitted}
                      className="px-5 py-3 rounded-xl bg-[#c79b5b] text-white hover:bg-[#a87f45] transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="submit-btn"
                      title={
                        !token
                          ? "Sign in to submit"
                          : completion < 100
                          ? `Complete ${100 - completion}% more to submit`
                          : submitted
                          ? "Already submitted"
                          : "Submit for WTTQ review"
                      }
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      {submitted ? "Submitted" : "Submit Assessment"}
                    </button>
                  </div>
                </div>
              </section>

              {/* Side panel */}
              <aside>
                <div className="q-main-card rounded-3xl p-6 sticky top-6">
                  <h2 className="q-serif text-xl text-[#102331]">Live Scoring Summary</h2>
                  <div className="flex justify-center mt-6">
                    <div
                      className="q-ring"
                      style={{
                        background: `conic-gradient(#c79b5b ${overall * 3.6}deg, #e8e1d6 0deg)`,
                      }}
                      data-testid="score-ring"
                    >
                      <div className="q-ring-inner">
                        <span className="text-3xl font-semibold" data-testid="overall-score-ring">
                          {overall}
                        </span>
                        <span className="text-xs text-[#7a858a] uppercase tracking-[0.16em]">
                          Score
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-5">
                    <p
                      className="text-lg font-semibold"
                      style={{ color: certification.color }}
                      data-testid="certification-main"
                    >
                      {certification.label}
                    </p>
                    <p className="text-sm text-[#7a858a] mt-1" data-testid="certification-sub">
                      {certification.sub}
                    </p>
                  </div>

                  <div className="mt-7 space-y-4" data-testid="breakdown-list">
                    {CATEGORIES.map((cat) => (
                      <div key={cat.id}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-[#5d6870]">{cat.name}</span>
                          <span className="font-semibold text-[#223845]">
                            {Math.round(categoryPercents[cat.id])}%
                          </span>
                        </div>
                        <div className="q-bar-track">
                          <div
                            className="q-bar-fill"
                            style={{ width: `${categoryPercents[cat.id]}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-7 p-4 rounded-2xl bg-[#fbf7f0] border border-[#eadfce]">
                    <h3 className="text-sm font-semibold text-[#6f5635] mb-3">
                      Certification Thresholds
                    </h3>
                    <div className="space-y-2 text-sm text-[#6c7377]">
                      <div>🥉 <strong>Bronze</strong> — 70+ overall, 60+ in each pillar</div>
                      <div>🥈 <strong>Silver</strong> — 80+ overall, 70+ in each pillar</div>
                      <div>🥇 <strong>Gold</strong> — 90+ overall, 85+ in each pillar + auditor review</div>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default QuestionnairePage;
